import { findBusinessBySelectionId } from "./businessDocumentContext";

export function normalizeRequestedFileCategory(value) {
  const s = String(value ?? "").trim().toLowerCase();
  return s === "business" ? "business" : "individual";
}

/** Category on a request row, with fallback when API omits it (legacy → individual). */
export function getRequestedFileCategory(row, fallback) {
  return normalizeRequestedFileCategory(
    row?.requested_file_category ??
      row?.document_category ??
      fallback ??
      "individual",
  );
}

export function getRequestedFileBusinessId(row) {
  const id = row?.client_business_id ?? row?.clientBusinessId;
  return id != null && String(id).trim() !== "" ? String(id).trim() : null;
}

export function resolveBusinessNameByClientBusinessId(
  businesses,
  clientBusinessId,
) {
  if (!clientBusinessId) return "";
  const match = findBusinessBySelectionId(businesses, clientBusinessId);
  return String(match?.business_name ?? "").trim();
}

export function getRequestedFileBusinessName(
  row,
  fallbackCategory,
  businesses = [],
) {
  const direct = String(row?.business_name ?? row?.businessName ?? "").trim();
  if (direct) return direct;

  const businessId = getRequestedFileBusinessId(row);
  if (businessId) {
    return resolveBusinessNameByClientBusinessId(businesses, businessId);
  }

  return "";
}

/** Navigation state for Documents → Requested tab from a requested-file row. */
export function buildRequestedFileDocumentsNavState(row, fallbackCategory) {
  const documentCategory = getRequestedFileCategory(row, fallbackCategory);
  const clientBusinessId = getRequestedFileBusinessId(row);
  return {
    documentsTab: "requested",
    documentCategory,
    ...(documentCategory === "business" && clientBusinessId
      ? { clientBusinessId }
      : {}),
  };
}

const nameFromUploadEntry = (entry) => {
  if (!entry) return "";
  if (typeof entry === "string") return entry.trim();
  return String(
    entry.file_name ??
      entry.document_name ??
      entry.name ??
      entry.uploaded_file_name ??
      "",
  ).trim();
};

/**
 * Collect all uploaded file names for a requested-file row (supports legacy single
 * fields and newer array / JSON shapes from the API).
 */
export function getUploadedFileNames(row) {
  if (!row) return [];

  if (Array.isArray(row.uploaded_file_names) && row.uploaded_file_names.length > 0) {
    return row.uploaded_file_names
      .map((n) => String(n ?? "").trim())
      .filter(Boolean);
  }

  const arraySources = [
    row.uploaded_files,
    row.uploads,
    row.uploaded_documents,
    row.requested_file_uploads,
    row.documents,
    row.files,
  ];

  for (const source of arraySources) {
    if (Array.isArray(source) && source.length > 0) {
      const names = source.map(nameFromUploadEntry).filter(Boolean);
      if (names.length) return names;
    }
  }

  if (typeof row.uploaded_file_names === "string" && row.uploaded_file_names.trim()) {
    const raw = row.uploaded_file_names.trim();
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map((n) => String(n ?? "").trim()).filter(Boolean);
      }
    } catch {
      /* plain string or comma-separated */
    }
    if (raw.includes(",")) {
      return raw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
    return [raw];
  }

  const single =
    row.uploaded_file_name ||
    row.uploaded_document_name ||
    row.uploaded_file ||
    "";
  if (!single || single === "—") return [];
  const trimmed = String(single).trim();
  if (trimmed.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.map((n) => String(n ?? "").trim()).filter(Boolean);
      }
    } catch {
      /* plain string */
    }
  }
  return [trimmed];
}

const toPositiveInt = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
};

/** Reminder interval in days from API row (supports snake_case / camelCase). */
export function getReminderFrequencyDays(row) {
  return toPositiveInt(
    row?.reminder_frequency_days ?? row?.reminderFrequencyDays,
  );
}

export function getReminderTotalCount(row) {
  return toPositiveInt(row?.reminder_total_count ?? row?.reminderTotalCount);
}

export function getRemindersSentCount(row) {
  const raw = row?.reminders_sent_count ?? row?.remindersSentCount;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : null;
}

export function isRequestedFileRemindersComplete(row) {
  if (!row) return true;
  if (row.reminders_completed === true || row.remindersCompleted === true) {
    return true;
  }
  const status = String(row.status || "").toLowerCase();
  if (status === "completed") return true;

  const sent = getRemindersSentCount(row);
  const total = getReminderTotalCount(row);
  if (sent != null && total != null && sent >= total) return true;

  return false;
}

export function getNextReminderAt(row) {
  const raw =
    row?.next_reminder_at ??
    row?.nextReminderAt ??
    row?.next_reminder_date ??
    row?.nextReminderDate;
  return raw && String(raw).trim() !== "" ? raw : null;
}

/** Staff table: e.g. "4 days" or "—" when unset. */
export function formatReminderFrequencyLabel(row) {
  const days = getReminderFrequencyDays(row);
  if (!days) return "—";
  return `${days} day${days === 1 ? "" : "s"}`;
}

/**
 * Staff table: next scheduled reminder, or "—" when none / finished.
 * Pass `formatDate` (e.g. dateFormatter) for display.
 */
export function formatNextReminderLabel(row, formatDate) {
  if (isRequestedFileRemindersComplete(row)) return "—";
  const nextAt = getNextReminderAt(row);
  if (!nextAt) return "—";
  const formatted = formatDate ? formatDate(nextAt) : String(nextAt);
  return formatted || "—";
}

/** Default max reminders per requested-file schedule. */
export const DEFAULT_REMINDER_TOTAL_COUNT = 5;

/**
 * Next reminder timestamp after an immediate first send (create / edit frequency).
 * Schedule matches new requests: send #1 now, then #2 at +frequency days.
 */
export function computeNextReminderAtAfterReset(
  frequencyDays,
  fromDate = new Date(),
) {
  const days = toPositiveInt(frequencyDays);
  if (!days) return null;
  const base = fromDate instanceof Date ? fromDate : new Date(fromDate);
  if (Number.isNaN(base.getTime())) return null;
  const next = new Date(base.getTime());
  next.setDate(next.getDate() + days);
  return next.toISOString();
}

/** Optional title tooltip: "2 of 5 reminders sent". */
export function getReminderProgressTitle(row) {
  const sent = getRemindersSentCount(row);
  const total = getReminderTotalCount(row);
  if (sent == null || total == null) return "";
  return `${sent} of ${total} reminders sent`;
}
